import { oc } from "@orpc/contract";
import { z } from "zod";
import { BusinessProfileStatus, ViesResult } from "./customer-account";
import { CustomerAddress } from "./customer-address";
import { listInput, listPage } from "./list";

/**
 * Customers in the admin (spec, "Klanten"; #76-#78): the list with guests and account holders,
 * one customer's data with the staff's notes, and anonymising a customer on request (AVG). Staff
 * only; nothing here is part of the shop API, notes least of all.
 */

/** `business`: has a business profile that is pending or approved. Everyone else is a consumer. */
export const CustomerKind = z.enum(["consumer", "business"]);
export type CustomerKind = z.infer<typeof CustomerKind>;

/** `guest`: no account (ordered without one). `unconfirmed`: registered, address not confirmed yet. */
export const CustomerAccountStatus = z.enum(["guest", "unconfirmed", "confirmed"]);
export type CustomerAccountStatus = z.infer<typeof CustomerAccountStatus>;

/** A customer group as a customer shows it: which business group's prices they get. */
export const CustomerGroupRef = z.object({ id: z.string(), name: z.string() });
export type CustomerGroupRef = z.infer<typeof CustomerGroupRef>;

export const CustomerListItem = z.object({
	id: z.string(),
	email: z.string(),
	name: z.string().nullable(),
	/** From the business profile, whatever its status. */
	companyName: z.string().nullable(),
	kind: CustomerKind,
	businessStatus: BusinessProfileStatus.nullable(),
	/** The business group of an approved business customer; null for everyone else. */
	customerGroup: CustomerGroupRef.nullable(),
	account: CustomerAccountStatus,
	/** ISO 8601, when the customer was anonymised. */
	anonymisedAt: z.iso.datetime().nullable(),
	createdAt: z.iso.datetime(),
});
export type CustomerListItem = z.infer<typeof CustomerListItem>;

export const CustomerListInput = listInput({
	sort: ["name", "email", "createdAt"],
	defaultSort: "createdAt",
	defaultOrder: "desc",
	filters: {
		kind: CustomerKind.optional().catch(undefined),
		/** Customers whose business application has this status (`pending`: still to assess). */
		businessStatus: BusinessProfileStatus.optional().catch(undefined),
		/** Approved business customers in this group. */
		customerGroupId: z.string().min(1).max(100).optional().catch(undefined),
	},
});
export type CustomerListInput = z.input<typeof CustomerListInput>;
/** The list query with its defaults filled in, as the API receives it. */
export type CustomerListQuery = z.output<typeof CustomerListInput>;

export const CustomerList = listPage(CustomerListItem);
export type CustomerList = z.infer<typeof CustomerList>;

/** A staff note on a customer, with who wrote it and when. */
export const CustomerNote = z.object({
	id: z.string(),
	body: z.string(),
	employee: z.object({ id: z.string(), name: z.string() }),
	createdAt: z.iso.datetime(),
});
export type CustomerNote = z.infer<typeof CustomerNote>;

/** The business profile as the admin sees it: the application, and what VIES said. */
export const AdminBusinessProfile = z.object({
	companyName: z.string(),
	kvkNumber: z.string(),
	vatId: z.string(),
	status: BusinessProfileStatus,
	vies: z.object({
		result: ViesResult,
		name: z.string().nullable(),
		address: z.string().nullable(),
		checkedAt: z.iso.datetime(),
	}),
	appliedAt: z.iso.datetime(),
	/** Approved: the business group whose prices the customer gets. */
	customerGroup: CustomerGroupRef.nullable(),
	/** Approved: days an invoice may stay open. */
	paymentTermDays: z.number().int().nullable(),
	/** Approved: btw verlegd, 0% VAT. */
	reverseCharge: z.boolean(),
	/**
	 * Whether reverse charge can be set on VIES's word: a Belgian VAT id that VIES found valid. The
	 * approve form suggests it when true.
	 */
	reverseChargeAllowed: z.boolean(),
	/**
	 * A Belgian VAT id that VIES could not check (`unknown`): reverse charge can still be set, once the
	 * employee confirms they checked the VAT id themselves (`vatIdCheckedManually`). Never for a Dutch
	 * VAT id or one VIES found invalid.
	 */
	reverseChargeNeedsConfirmation: z.boolean(),
	/** Reverse charge on the employee's own check of the VAT id: who confirmed it, and when. */
	reverseChargeConfirmation: z
		.object({
			confirmedAt: z.iso.datetime(),
			confirmedBy: z.object({ id: z.string(), name: z.string() }),
		})
		.nullable(),
	/** Rejected: the reason the customer was mailed. */
	rejectionReason: z.string().nullable(),
	/** When an employee approved or rejected the application, and who. */
	decidedAt: z.iso.datetime().nullable(),
	decidedBy: z.object({ id: z.string(), name: z.string() }).nullable(),
});
export type AdminBusinessProfile = z.infer<typeof AdminBusinessProfile>;

export const CustomerDetail = z.object({
	id: z.string(),
	email: z.string(),
	/** The display name (first and last name joined). */
	name: z.string().nullable(),
	/** From registration; null for a guest and after anonymising. */
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	phone: z.string().nullable(),
	/** The address book: the defaults first, then oldest first. Empty after anonymising. */
	addresses: z.array(CustomerAddress),
	kind: CustomerKind,
	account: z.object({
		status: CustomerAccountStatus,
		/** When the account was registered; null for a guest. */
		createdAt: z.iso.datetime().nullable(),
		emailConfirmedAt: z.iso.datetime().nullable(),
	}),
	/** Whether the customer's address has an active newsletter opt-in. */
	newsletter: z.boolean(),
	businessProfile: AdminBusinessProfile.nullable(),
	/** Newest first. */
	notes: z.array(CustomerNote),
	anonymisedAt: z.iso.datetime().nullable(),
	createdAt: z.iso.datetime(),
});
export type CustomerDetail = z.infer<typeof CustomerDetail>;

export const CustomerPath = z.object({ customerId: z.string() });

export const AddCustomerNoteBody = z.object({ body: z.string().trim().min(1).max(2000) });
export type AddCustomerNoteBody = z.infer<typeof AddCustomerNoteBody>;

/** The payment term an approval proposes (spec: standard 30 days). */
export const DEFAULT_PAYMENT_TERM_DAYS = 30;

/** Approving a business application (#80), or changing an approved customer's terms. */
export const ApproveBusinessBody = z.object({
	/** A business group; Consument is refused. */
	customerGroupId: z.string().min(1),
	paymentTermDays: z.number().int().min(0).max(365),
	/**
	 * Only for a Belgian VAT id that VIES found valid (`reverseChargeAllowed`), or that VIES could not
	 * check (`reverseChargeNeedsConfirmation`) with `vatIdCheckedManually`.
	 */
	reverseCharge: z.boolean(),
	/**
	 * "Ik heb het btw-nummer zelf gecontroleerd": the employee's confirmation that makes reverse charge
	 * possible when VIES could not check the VAT id. Stored with who and when. Not needed again when
	 * changing the terms of a customer whose reverse charge was confirmed before.
	 */
	vatIdCheckedManually: z.boolean().optional(),
});
export type ApproveBusinessBody = z.infer<typeof ApproveBusinessBody>;

/** Rejecting a business application (#80): the reason goes to the customer in the mail. */
export const RejectBusinessBody = z.object({ reason: z.string().trim().min(1).max(1000) });
export type RejectBusinessBody = z.infer<typeof RejectBusinessBody>;

/** Customers, for signed-in employees only. */
export const CustomerContract = {
	/** A page of customers, guests and account holders: search on name, email and company, filter on kind. */
	list: oc.route({ method: "GET", path: "/admin/customers" }).input(CustomerListInput).output(CustomerList),
	/** One customer with their notes. Errors: `CustomerNotFound` (404). */
	get: oc
		.route({ method: "GET", path: "/admin/customers/{customerId}" })
		.input(CustomerPath)
		.output(CustomerDetail),
	/**
	 * Adds a note, signed with the employee and the time. Errors: `CustomerNotFound` (404),
	 * `CustomerAnonymised` (409).
	 */
	addNote: oc
		.route({ method: "POST", path: "/admin/customers/{customerId}/notes" })
		.input(CustomerPath.extend(AddCustomerNoteBody.shape))
		.output(CustomerNote),
	/**
	 * Anonymises the customer (AVG): name, phone, email address and saved addresses gone, the
	 * account deleted (it cannot log in any more), the newsletter opt-in and the notes deleted, and
	 * the recipient in the mail log blanked. The business profile stays (company, KvK/KBO, VAT id,
	 * VIES result): company data, not personal data. Orders and invoices keep their snapshots. Once only:
	 * `CustomerAnonymised` (409) the second time; `CustomerNotFound` (404).
	 */
	anonymise: oc
		.route({ method: "POST", path: "/admin/customers/{customerId}/anonymise" })
		.input(CustomerPath)
		.output(CustomerDetail),
	/**
	 * Approves the customer's pending business application with a business group, payment term and
	 * reverse charge, and mails the customer. On an approved customer it changes those terms, without
	 * a mail. Errors: `CustomerNotFound` (404), `BusinessProfileNotFound` (404),
	 * `BusinessDecisionNotAllowed` (409: rejected, or anonymised), `BusinessGroupInvalid` (400: no such
	 * business group), `ReverseChargeNotAllowed` (400: a Dutch VAT id, or one VIES found invalid),
	 * `ReverseChargeConfirmationRequired` (400: VIES could not check the Belgian VAT id, and
	 * `vatIdCheckedManually` is missing).
	 */
	approveBusiness: oc
		.route({ method: "POST", path: "/admin/customers/{customerId}/business/approve" })
		.input(CustomerPath.extend(ApproveBusinessBody.shape))
		.output(CustomerDetail),
	/**
	 * Rejects the customer's pending business application with a reason, and mails the customer.
	 * They may apply again from the shop. Errors: `CustomerNotFound` (404), `BusinessProfileNotFound`
	 * (404), `BusinessDecisionNotAllowed` (409: not pending).
	 */
	rejectBusiness: oc
		.route({ method: "POST", path: "/admin/customers/{customerId}/business/reject" })
		.input(CustomerPath.extend(RejectBusinessBody.shape))
		.output(CustomerDetail),
};
